import { html } from "htm/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { NotJoined } from "../components/NotJoined.js";
import { loadCourse } from "../courseData.js";
import { navigate } from "../router.js";
import { getAuth } from "../storage.js";
import { computeLiveRoundResults } from "../../../shared/leaderboard.js";
import { computeFinalStandings } from "../../../shared/standings.js";
import { explainTotal } from "../../../shared/explain.js";
import { isDon } from "../../../shared/donMode.js";
import { armDonMode, useHold } from "../donMode.js";
import { Icon } from "../components/Icon.js";
import { TERMS } from "../copy.js";
import type { Course } from "../../../shared/types.js";

function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${suffix}`;
}

const TIEBREAK_TEXT: Record<string, string> = {
  split: "Ties share the points for the places they cover.",
  mostRoundWins: "Players level on points are separated by most round wins.",
  bestSingleRound: "Players level on points are separated by their best single round.",
  lowestNetTotal: "Players level on points are separated by lowest net total.",
  manual: `Players level on points are ordered by the ${TERMS.organizer}.`,
};

/**
 * Don mode's front door — the app's easter egg (`shared/donMode.ts`), offered exactly
 * once, to exactly one person, on the one screen that is already his.
 *
 * It lives here rather than on the standings because the standings had nowhere to say
 * what to do. A hidden press-and-hold on his row went undiscovered through two rounds
 * of louder and louder hints, which is what a wordless cue does to a gesture nobody is
 * expecting. A page belonging to one player can just tell him.
 *
 * Nobody else's device renders this — not his playing partners, not the organiser —
 * so it stays a thing he found rather than a feature the group has.
 */
function DonPanel({ onFire }: { onFire: () => void }) {
  const [nagging, setNagging] = useState(false);
  const hold = useHold(onFire, () => {
    // A button that says "hold" and does nothing when tapped reads as broken. Saying
    // so is also the only instruction anybody needs.
    setNagging(true);
    setTimeout(() => setNagging(false), 2000);
  });

  return html`
    <div class="panel don-panel">
      <div class="row row-between">
        <span class="eyebrow">Don mode</span>
        <span class="chip">one per ${TERMS.event}</span>
      </div>
      <p class="field-help mt-0">They all owe you one anyway.</p>
      <button class="don-fire" ...${hold}>
        ${nagging ? "Hold it. Don't let go." : "Hold to take everything"}
      </button>
    </div>
  `;
}

/** Where a player's total came from: every round, and what each one contributed. */
export function PlayerTotal({ code, playerId }: { code: string; playerId: string }) {
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const [courses, setCourses] = useState<Map<string, Course>>(new Map());

  const courseIds = useMemo(
    () => [...new Set((snapshot?.tournament.rounds ?? []).map((r) => r.courseId))].filter(Boolean),
    [snapshot],
  );

  useEffect(() => {
    Promise.all(courseIds.map((id) => loadCourse(id))).then((loaded) => {
      const map = new Map<string, Course>();
      loaded.forEach((c) => c && map.set(c.id, c));
      setCourses(map);
    });
  }, [courseIds.join(",")]);

  if (!client) return html`<${NotJoined} code=${code} />`;
  if (!snapshot) return html`<p class="muted">Loading…</p>`;

  const { tournament } = snapshot;
  const results = computeLiveRoundResults(tournament, courses, snapshot.scores, snapshot.perHoleData);
  const standings = computeFinalStandings(
    tournament.players.map((p) => p.id),
    results.filter((r) => !r.provisional),
    tournament.finalTiebreaker,
  );
  const placement = standings.find((s) => s.playerId === playerId)?.placement ?? null;
  const x = explainTotal(
    tournament,
    courses,
    snapshot.scores,
    snapshot.perHoleData,
    playerId,
    placement,
  );

  // His page, his device, his name, and the game's one go still unspent. The Durable
  // Object enforces every one of these from the token that sends the op — this is only
  // what decides whether anyone is shown the offer.
  const donsOwnPage =
    !tournament.donMode && playerId === getAuth(code)?.playerId && isDon(x.playerName);

  return html`
    <p class="back-link">
      <a href=${`#/t/${code}/leaderboard`}><${Icon} name="left" size=${16} /> Standings</a>
    </p>

    <h1 class="mb-0">${x.playerName}</h1>
    <p class="course-line">
      ${placement ? `Currently ${ordinal(placement)}` : "Not placed yet"}${" · "}
      ${`${x.total} point${x.total === 1 ? "" : "s"} confirmed`}
    </p>

    ${donsOwnPage &&
    html`<${DonPanel}
      onFire=${() => {
        // Armed rather than sent: the board fires it once it's mounted, so the first
        // beat of the theft happens in front of him instead of before he arrives.
        armDonMode();
        navigate(`/t/${code}/leaderboard`);
      }}
    />`}

    <div class="panel">
      <span class="eyebrow">Round by round</span>
      <p class="field-help mt-0">
        Tap a round for its own breakdown — every hole, the strokes your handicap gave you and
        where they landed, and how that became points.
      </p>
      <div class="calc">
        ${x.rounds.map(
          (r) => html`
            <button
              class=${`calc-row calc-link ${r.provisional || !r.started ? "is-open" : ""}`}
              key=${r.roundId}
              onClick=${() => navigate(`/t/${code}/round/${r.roundId}/player/${playerId}`)}
            >
              <span class="calc-label">
                ${r.roundName}
                ${r.note
                  ? html`<small>${r.note}</small>`
                  : r.multiplier !== 1
                    ? html`<small>${`counts ×${r.multiplier}`}</small>`
                    : null}
              </span>
              <span class="calc-value">
                ${r.provisional || !r.started ? html`<span class="muted-points">${r.points}</span>` : r.points}
                <${Icon} name="right" size=${16} />
              </span>
            </button>
          `,
        )}

        <div class="calc-row calc-total">
          <span class="calc-label">Confirmed total</span>
          <span class="calc-value">${x.total}</span>
        </div>
      </div>

      ${x.projected > 0 &&
      html`<p class="field-help mb-0">
        ${`A further `}<strong>${x.projected}</strong>
        ${` ${x.projected === 1 ? "point is" : "points are"} riding on rounds that are still open.`}
        ${` They join the total once the ${TERMS.organizer} closes those rounds.`}
      </p>`}
    </div>

    <div class="panel">
      <span class="eyebrow">Ties</span>
      <p class="field-help mt-0 mb-0">
        ${TIEBREAK_TEXT[x.tiebreaker] ?? TIEBREAK_TEXT.split}
      </p>
    </div>
  `;
}
